import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Row, 
    Col, 
    Card, 
    Form, 
    Button, 
    Alert, 
    Spinner 
} from 'react-bootstrap';
import { 
    PersonFill, 
    LockFill, 
    EyeFill, 
    EyeSlashFill,
    ShieldLockFill 
} from 'react-bootstrap-icons';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const Login = () => {
    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    
    const { login, isAuthenticated } = useAuth();
    const navigate = useNavigate();

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/admin', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.username.trim() || !formData.password) {
            setError('Please enter both username and password');
            return;
        }

        if (formData.password.length < 3) {
            setError('Password must be at least 3 characters long');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const result = await login(formData.username.trim(), formData.password);
            
            if (result.success) {
                // Clear form data on successful login
                setFormData({ username: '', password: '' });
                navigate('/admin', { replace: true });
            } else {
                setError(result.message || 'Invalid username or password');
            }
        } catch (error) {
            setError('Unable to connect to server. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !isLoading) {
            handleSubmit(e);
        }
    };

    return (
        <div 
            className="login-page d-flex align-items-center justify-content-center min-vh-100 bg-primary"
            style={{ padding: '20px' }}
        >
            <Container>
                <Row className="justify-content-center">
                    <Col xs={12} sm={10} md={8} lg={6} xl={4}>
                        <Card 
                            className="shadow-lg border-0 animate__animated animate__fadeIn" 
                            style={{ 
                                borderRadius: '20px',
                                backdropFilter: 'blur(10px)',
                                backgroundColor: 'rgba(255, 255, 255, 0.95)'
                            }}
                        >
                            <Card.Body className="p-4 p-md-5">
                                {/* Header */}
                                <div className="text-center mb-4">
                                    <div className="mb-3">
                                        <div 
                                            className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10" 
                                            style={{ width: '80px', height: '80px' }}
                                        >
                                            <ShieldLockFill 
                                                size={48} 
                                                className="text-primary"
                                            />
                                        </div>
                                    </div>
                                    <h2 className="fw-bold text-dark mb-2">Welcome Back</h2>
                                    <p className="text-muted mb-0">
                                        Sign in to Interview Scheduler
                                    </p>
                                </div>

                                {/* Error Alert (stays until cleared) */}
                                {error && (
                                    <Alert 
                                        variant="danger" 
                                        className="py-2 mb-3 border-0"
                                        style={{
                                            borderRadius: '12px',
                                            backgroundColor: 'rgba(220, 53, 69, 0.1)',
                                            borderLeft: '4px solid #dc3545'
                                        }}
                                    >
                                        <small className="fw-medium">{error}</small>
                                    </Alert>
                                )}

                                {/* Login Form */}
                                <Form onSubmit={handleSubmit} onKeyPress={handleKeyPress}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold text-dark">
                                            <PersonFill className="me-2" />
                                            Username
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="username"
                                            value={formData.username}
                                            onChange={handleChange}
                                            placeholder="Enter your username"
                                            size="lg"
                                            disabled={isLoading}
                                            autoComplete="username"
                                            style={{ 
                                                borderRadius: '12px',
                                                border: '2px solid #e9ecef',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#0d6efd'}
                                            onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="fw-semibold text-dark">
                                            <LockFill className="me-2" />
                                            Password
                                        </Form.Label>
                                        <div className="position-relative">
                                            <Form.Control
                                                type={showPassword ? 'text' : 'password'}
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                placeholder="Enter your password"
                                                size="lg"
                                                disabled={isLoading}
                                                autoComplete="current-password"
                                                style={{ 
                                                    borderRadius: '12px',
                                                    paddingRight: '50px',
                                                    border: '2px solid #e9ecef',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onFocus={(e) => e.target.style.borderColor = '#0d6efd'}
                                                onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                                            />
                                            <Button
                                                type="button"
                                                variant="link"
                                                className="position-absolute end-0 top-50 translate-middle-y me-2"
                                                style={{ 
                                                    border: 'none', 
                                                    background: 'none',
                                                    padding: '8px',
                                                    borderRadius: '8px'
                                                }}
                                                onClick={() => setShowPassword(!showPassword)}
                                                disabled={isLoading}
                                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                            >
                                                {showPassword ? 
                                                    <EyeSlashFill className="text-muted" size={18} /> : 
                                                    <EyeFill className="text-muted" size={18} />
                                                }
                                            </Button>
                                        </div>
                                    </Form.Group>

                                    <Button
                                        type="submit"
                                        variant="primary"
                                        size="lg"
                                        className="w-100 fw-bold"
                                        disabled={isLoading || !formData.username.trim() || !formData.password}
                                        style={{ 
                                            borderRadius: '12px', 
                                            padding: '14px',
                                            fontSize: '16px',
                                            transition: 'all 0.3s ease',
                                            background: 'linear-gradient(135deg, #0d6efd 0%, #0056b3 100%)',
                                            border: 'none'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isLoading) {
                                                e.target.style.transform = 'translateY(-1px)';
                                                e.target.style.boxShadow = '0 8px 25px rgba(13, 110, 253, 0.3)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = 'none';
                                        }}
                                    >
                                        {isLoading ? (
                                            <>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    className="me-2"
                                                />
                                                Signing in...
                                            </>
                                        ) : (
                                            'Sign In'
                                        )}
                                    </Button>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Login;
